// Translation catalogs. Keys are flat, dot-namespaced.
// EN and PT are complete; ES and IT cover navigation, chrome and key marketing
// strings. Missing keys fall back to English (see i18n/index.js).

export const LOCALES = {
  /* ===================================================================== */
  /* ENGLISH (complete, canonical)                                          */
  /* ===================================================================== */
  en: {
    // ---- Top bar / chrome ----
    "topbar.tagline": "Verifiable integrity, at any scale.",
    "topbar.status": "Enterprise-grade · SOC 2-ready · LGPD & GDPR",

    // ---- Navigation ----
    "nav.home": "Home",
    "nav.exams": "Exams",
    "nav.ctf": "CTF + LiveStream",
    "nav.features": "Features",
    "nav.pricing": "Pricing",
    "nav.security": "Security",
    "nav.integrations": "Integrations",
    "nav.docs": "Docs / API",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "nav.login": "Log in",
    "nav.getStarted": "Get started",
    "nav.bookDemo": "Book a demo",
    "nav.openApp": "Open app",
    "nav.menu": "Menu",

    // ---- Common ----
    "common.learnMore": "Learn more",
    "common.startFree": "Start free trial",
    "common.bookDemo": "Book a demo",
    "common.contactSales": "Contact sales",
    "common.talkToUs": "Talk to us",
    "common.readMore": "Read more",
    "common.viewAll": "View all",
    "common.all": "All",
    "common.recommended": "Recommended",
    "common.monthly": "Monthly",
    "common.annual": "Annual",
    "common.annualHint": "2 months free",
    "common.perMonth": "/mo",
    "common.close": "Close",
    "common.cancel": "Cancel",
    "common.confirm": "Confirm",
    "common.save": "Save",
    "common.delete": "Delete",
    "common.remove": "Remove",
    "common.search": "Search",
    "common.export": "Export",
    "common.filter": "Filter",
    "common.loading": "Loading…",
    "common.new": "New",
    "common.backHome": "Back to home",
    "common.language": "Language",
    "common.theme": "Theme",
    "common.darkMode": "Dark mode",
    "common.lightMode": "Light mode",

    // ---- Home / hero ----
    "home.hero.eyebrow": "Remote proctoring platform",
    "home.hero.title1": "Verifiable integrity,",
    "home.hero.titleHighlight": "at any scale.",
    "home.hero.lead":
      "Continuous screen + camera + audio capture with tamper-proof server timestamps, no-re-encode recording, AI-assisted review and low-latency live streaming — engineered to scale from a single session to large simultaneous events.",
    "home.hero.ctaFree": "Start free trial",
    "home.hero.ctaDemo": "Book a demo",
    "home.hero.badge1": "sessions monitored",
    "home.hero.badge2": "hours recorded",
    "home.hero.badge3": "simultaneous streams",
    "home.hero.livePanel": "Live monitoring",
    "home.hero.liveTiles": "live grid · sharded",

    // ---- Home / verticals ----
    "home.verticals.eyebrow": "Choose your vertical",
    "home.verticals.title": "One core engine, two purpose-built experiences",
    "home.verticals.subtitle":
      "Exam proctoring and CTF anti-cheat share the same tamper-proof capture core — with distinct visual language, workflows and use cases.",
    "home.verticals.exams.tag": "For exams & certifications",
    "home.verticals.exams.title": "Exam Proctoring",
    "home.verticals.exams.desc":
      "Monitor exams, certifications, public tenders and academic or corporate assessments with guided identity check, AI event detection and per-candidate integrity reports.",
    "home.verticals.exams.cta": "Explore Exams",
    "home.verticals.ctf.tag": "For CTF championships",
    "home.verticals.ctf.title": "CTF + LiveStream",
    "home.verticals.ctf.desc":
      "Anti-cheat monitoring for cybersecurity competitions with an Event Wall, CTFd/rCTF integration, broadcast overlays and low-latency OBS output.",
    "home.verticals.ctf.cta": "Explore CTF",

    // ---- Home / features ----
    "home.features.eyebrow": "Why SecProctoring",
    "home.features.title": "The technical core that makes evidence hold up",
    "home.features.subtitle": "Built for scale, fidelity and forensic-grade integrity.",
    "feat.capture.title": "Continuous capture",
    "feat.capture.desc": "Screen, webcam and ambient audio recorded uninterrupted from start to finish.",
    "feat.timestamp.title": "Tamper-proof timestamp",
    "feat.timestamp.desc": "Server clock only — evidence never trusts the client’s time.",
    "feat.noreencode.title": "No-re-encode recording",
    "feat.noreencode.desc": "Gap-free source files; overlay and encode deferred to a separate service.",
    "feat.ai.title": "AI-assisted review",
    "feat.ai.desc": "Absent face, multiple people, gaze off-screen, tab switches, secondary devices.",
    "feat.scale.title": "Massive scale",
    "feat.scale.desc": "Horizontal sharding for live relay and recording, distributed by UUID.",
    "feat.live.title": "Low-latency live",
    "feat.live.desc": "IP-gated public output and native OBS integration for broadcast.",
    "feat.crossos.title": "Cross-OS, no install",
    "feat.crossos.desc": "Browser capture over WebSocket/WebCodecs — nothing to install, any OS.",
    "feat.telemetry.title": "Per-session telemetry",
    "feat.telemetry.desc": "Ingest throughput, negotiated codec, stalled-source detection with root cause.",

    // ---- Home / stats ----
    "home.stats.eyebrow": "Impact in numbers",
    "home.stats.sessions": "Sessions monitored",
    "home.stats.hours": "Hours recorded",
    "home.stats.frauds": "Fraud events flagged",
    "home.stats.uptime": "Ingestion uptime",

    // ---- Home / how it works ----
    "home.how.eyebrow": "How it works",
    "home.how.title": "From check-in to verdict in four steps",
    "home.how.s1.title": "Check-in & identity",
    "home.how.s1.desc": "Document photo + selfie, facial match and pre-session device test.",
    "home.how.s2.title": "Continuous capture",
    "home.how.s2.desc": "Screen + camera + audio stream to ingest with resume tokens and keyframes.",
    "home.how.s3.title": "AI + human review",
    "home.how.s3.desc": "Event timeline with severity markers; proctors monitor a live grid.",
    "home.how.s4.title": "Integrity report",
    "home.how.s4.desc": "Per-session score, evidence clips and a final verdict, exportable.",

    // ---- Home / compliance ----
    "home.compliance.eyebrow": "Security & compliance",
    "home.compliance.title": "Trust built into every layer",
    "home.compliance.subtitle":
      "Encryption in transit and at rest, clear recording notice to monitored users, retention and export controls.",
    "home.compliance.lgpd": "LGPD & GDPR aligned",
    "home.compliance.soc2": "SOC 2-ready controls",
    "home.compliance.encryption": "TLS in transit · AES at rest",
    "home.compliance.dpa": "DPA available",

    "home.cta.title": "Ready to prove integrity at scale?",
    "home.cta.subtitle": "Spin up a pilot in minutes or talk to our team about enterprise scale.",
    "home.video.eyebrow": "See it in action",
    "home.video.title": "Watch SecProctoring in action",
    "home.video.subtitle": "A quick look at continuous capture, live monitoring and the Event Wall.",

    // ---- Trusted by ----
    "home.trusted.title": "Trusted by teams running high-stakes assessments and competitions",

    // ---- Exams page ----
    "exams.hero.eyebrow": "Exam Proctoring",
    "exams.hero.title": "Fair exams, defensible evidence",
    "exams.hero.lead":
      "For universities, certification bodies, corporate HR and public-tender authorities: guided identity verification, continuous capture and AI event detection with per-candidate integrity reports.",
    "exams.f.identity.title": "Candidate identity verification",
    "exams.f.identity.desc": "Document photo + selfie, facial comparison and a guided check-in flow.",
    "exams.f.preflight.title": "Pre-exam environment check",
    "exams.f.preflight.desc": "Test camera, microphone, screen and connection speed before starting.",
    "exams.f.capture.title": "Screen + webcam + audio",
    "exams.f.capture.desc": "Simultaneous, continuous capture from start to end of the exam.",
    "exams.f.timestamp.title": "Server timestamp overlay",
    "exams.f.timestamp.desc": "Tamper-proof evidence — never trusts the client clock.",
    "exams.f.ai.title": "AI event detection",
    "exams.f.ai.desc": "Absent face, multiple people, gaze off-screen, tab switch, secondary devices, copy/paste.",
    "exams.f.modes.title": "Live, recorded or hybrid",
    "exams.f.modes.desc": "A human proctor monitors a grid, review later, or both.",
    "exams.f.review.title": "Post-exam review queue",
    "exams.f.review.desc": "Event timeline, severity filters and precise clip export.",
    "exams.f.rules.title": "Per-exam rules",
    "exams.f.rules.desc": "Required audio, resolution, tolerances, schedule and duration.",
    "exams.compare.title": "With vs. without SecProctoring",
    "exams.compare.without": "Without",
    "exams.compare.with": "With SecProctoring",
    "exams.faq.title": "Exam proctoring FAQ",

    // ---- CTF page ----
    "ctf.hero.eyebrow": "CTF + LiveStream",
    "ctf.hero.title": "Anti-cheat and broadcast for cyber competitions",
    "ctf.hero.lead":
      "Monitor competitors during the CTF, drive a public Event Wall with a live scoreboard, and stream to your audience with low latency via OBS.",
    "ctf.f.anticheat.title": "Anti-cheat monitoring",
    "ctf.f.anticheat.desc": "Screen + camera + audio ensure solves are the competitor’s own work.",
    "ctf.f.integration.title": "CTFd / rCTF integration",
    "ctf.f.integration.desc": "Real-time ranking and position via native integration or webhook.",
    "ctf.f.eventwall.title": "Event Wall",
    "ctf.f.eventwall.desc": "Public/private wall with Top 10 spotlight and All presets, switched live.",
    "ctf.f.stream.title": "Low-latency LiveStream",
    "ctf.f.stream.desc": "IP-gated public output and native OBS integration for broadcast.",
    "ctf.f.overlays.title": "Broadcast overlays",
    "ctf.f.overlays.desc": "Live scoreboard, competitor handle, flag-capture spotlight and event timer.",
    "ctf.f.cast.title": "Cast button",
    "ctf.f.cast.desc": "Swap the broadcast template/scene in real time from the console.",
    "ctf.f.scale.title": "Championship scale",
    "ctf.f.scale.desc": "Simultaneous streams and full recording for post-event audit.",
    "ctf.audience.organizers": "For organizers",
    "ctf.audience.public": "For the audience",

    // ---- Features page ----
    "features.hero.eyebrow": "Technical core",
    "features.hero.title": "Engineered for forensic-grade integrity",
    "features.hero.lead": "The capabilities behind every SecProctoring session — accessible, with a “for engineers” deep dive.",
    "features.forEngineers": "For engineers",
    "features.arch.title": "Architecture at a glance",
    "features.arch.client": "Client (browser)",
    "features.arch.ingest": "Ingest (WebSocket/ASGI)",
    "features.arch.recording": "Recording (raw buckets)",
    "features.arch.renderer": "Renderer (overlay + encode)",
    "features.arch.review": "Review / DB",
    "features.arch.relay": "Live relay",
    "features.arch.output": "Event Wall / OBS",

    // ---- Pricing ----
    "pricing.hero.eyebrow": "Pricing",
    "pricing.hero.title": "Plans that scale with your integrity needs",
    "pricing.hero.lead": "Switch between verticals and billing cycles. Enterprise scale is always a conversation away.",
    "pricing.toggle.exams": "Exams",
    "pricing.toggle.ctf": "CTF + LiveStream",
    "pricing.mostPopular": "Most popular",
    "pricing.customPrice": "Let’s talk",
    "pricing.cta.start": "Start free",
    "pricing.cta.contact": "Contact sales",
    "pricing.compare.title": "Compare plans",
    "pricing.faq.title": "Billing FAQ",

    // Exams plans
    "plan.starter.name": "Starter",
    "plan.starter.desc": "Recording + review for small teams.",
    "plan.pro.name": "Pro",
    "plan.pro.desc": "AI detection, live + recorded, LMS/SSO.",
    "plan.business.name": "Business",
    "plan.business.desc": "Multi-tenant, API/webhooks, SLA.",
    "plan.enterprise.name": "Enterprise",
    "plan.enterprise.desc": "Massive scale, on-prem, DPA, SAML.",
    // CTF plans
    "plan.community.name": "Community",
    "plan.community.desc": "Small events, basic public live.",
    "plan.competition.name": "Competition",
    "plan.competition.desc": "Event Wall, CTFd/rCTF, OBS output.",
    "plan.championship.name": "Championship",
    "plan.championship.desc": "Large simultaneous events.",
    "plan.whitelabel.name": "Enterprise / White-label",
    "plan.whitelabel.desc": "Own branding, dedicated, custom.",

    // ---- Security page ----
    "security.hero.eyebrow": "Security & Compliance",
    "security.hero.title": "Evidence you can defend, data you can trust",
    "security.hero.lead": "Security architecture, policies, certifications and a Data Processing Agreement.",

    // ---- Contact / demo ----
    "contact.hero.eyebrow": "Contact & demo",
    "contact.hero.title": "See SecProctoring on your own exams or event",
    "contact.hero.lead": "Tell us about your use case and we’ll tailor a demo. Reach us by email or WhatsApp — we reply within one business day.",
    "contact.channels.title": "Choose how to reach us",
    "contact.channels.subtitle": "No forms. Email us or start a WhatsApp chat — we reply within one business day.",
    "contact.email.title": "Email",
    "contact.email.cta": "Send email",
    "contact.whatsapp.title": "WhatsApp",
    "contact.whatsapp.desc": "Chat with our team now.",
    "contact.whatsapp.cta": "Open WhatsApp",
    "contact.form.name": "Full name",
    "contact.form.email": "Work email",
    "contact.form.company": "Company",
    "contact.form.vertical": "Vertical of interest",
    "contact.form.verticalExams": "Exam proctoring",
    "contact.form.verticalCtf": "CTF + LiveStream",
    "contact.form.verticalBoth": "Both",
    "contact.form.scale": "Expected scale (simultaneous sessions)",
    "contact.form.message": "How can we help?",
    "contact.form.submit": "Request demo",
    "contact.form.consent": "I agree to the processing of my data per the Privacy Policy.",
    "contact.success.title": "Request received",
    "contact.success.body": "Thanks! Our team will reach out within one business day to schedule your demo.",

    // ---- Auth ----
    "auth.login.title": "Log in to SecProctoring",
    "auth.login.subtitle": "Access your company workspace.",
    "auth.login.email": "Email",
    "auth.login.password": "Password",
    "auth.login.remember": "Keep me signed in",
    "auth.login.forgot": "Forgot password?",
    "auth.login.submit": "Log in",
    "auth.login.sso": "Continue with SSO",
    "auth.login.mfaNote": "Protected by MFA",
    "auth.login.noAccount": "No account?",
    "auth.login.demoHint": "Demo build — any email/password enters the app.",

    // ---- App shell ----
    "app.nav.overview": "Overview",
    "app.nav.monitoring": "Live Monitoring",
    "app.nav.eventWall": "Event Wall",
    "app.nav.sessions": "Sessions",
    "app.nav.review": "Session Review",
    "app.nav.examsEvents": "Exams / Events",
    "app.nav.people": "Candidates / Competitors",
    "app.nav.stream": "Stream Config",
    "app.nav.reports": "Reports",
    "app.nav.alerts": "Alerts & Telemetry",
    "app.nav.admin": "Administration",
    "app.nav.account": "Account settings",
    "app.nav.backToSite": "Back to website",
    "app.nav.logout": "Log out",
    "app.company": "Company",
    "app.notifications": "Notifications",

    // Dashboard
    "dash.title": "Overview",
    "dash.subtitle": "Real-time posture across your tenant.",
    "dash.kpi.activeSessions": "Active sessions",
    "dash.kpi.liveStreams": "Live streams",
    "dash.kpi.openAlerts": "Open alerts",
    "dash.kpi.hoursRecorded": "Hours recorded",
    "dash.kpi.throughput": "Avg throughput (1 min)",
    "dash.chart.sessions": "Sessions over time",
    "dash.recentEvents": "Recent events",
    "dash.systemHealth": "System health",
    "dash.shards": "Shards",
    "dash.ingest": "Ingest",
    "dash.renderer": "Renderer",
    "dash.relay": "Live relay",

    // Monitoring
    "mon.title": "Live Monitoring",
    "mon.subtitle": "Camera + screen tiles across active sessions.",
    "mon.layout": "Layout",
    "mon.focus": "Focus",
    "mon.searchPlaceholder": "Search candidate or session…",
    "mon.alert": "Alert",
    "mon.healthy": "Healthy",
    "mon.cast": "Cast",
    "mon.preset": "Preset",
    "mon.presetTop10": "Top 10 spotlight",
    "mon.presetAll": "All",
    "mon.score": "Score",

    // Sessions
    "sessions.title": "Sessions",
    "sessions.subtitle": "Every capture session across exams and events.",
    "sessions.col.subject": "Candidate / Competitor",
    "sessions.col.exam": "Exam / Event",
    "sessions.col.company": "Company",
    "sessions.col.status": "Status",
    "sessions.col.start": "Start",
    "sessions.col.duration": "Duration",
    "sessions.col.events": "Events",
    "sessions.col.integrity": "Integrity",
    "sessions.status.live": "Live",
    "sessions.status.recorded": "Recorded",
    "sessions.status.review": "In review",
    "sessions.status.done": "Completed",
    "sessions.bulkExport": "Export selected",

    // Review
    "review.title": "Session Review",
    "review.timeline": "Event timeline",
    "review.events": "Events",
    "review.sources": "Sources",
    "review.screen": "Screen",
    "review.camera": "Camera",
    "review.audio": "Audio",
    "review.clip": "Export clip",
    "review.notes": "Reviewer notes",
    "review.verdict": "Integrity verdict",
    "review.verdict.clean": "Clean",
    "review.verdict.review": "Needs review",
    "review.verdict.flagged": "Flagged",
    "review.saveVerdict": "Save verdict",

    // Exams/Events CRUD
    "ee.title": "Exams / Events",
    "ee.new": "New exam / event",
    "ee.form.name": "Name",
    "ee.form.company": "Company",
    "ee.form.schedule": "Schedule",
    "ee.form.duration": "Duration (min)",
    "ee.form.mode": "Mode",
    "ee.form.modeLive": "Live",
    "ee.form.modeRecorded": "Recorded",
    "ee.form.modeHybrid": "Hybrid",
    "ee.form.requireAudio": "Require audio",
    "ee.form.resolution": "Max resolution",
    "ee.form.tolerance": "AI tolerance",
    "ee.form.integrations": "Integrations",
    "ee.form.save": "Save exam / event",

    // Stream config
    "stream.title": "Stream Configuration",
    "stream.subtitle": "OBS outputs, public IP-gated URL and scene templates.",
    "stream.ndi": "NDI (LAN)",
    "stream.srt": "SRT / RTSP (remote)",
    "stream.public": "Public IP-gated URL",
    "stream.templates": "Scene templates",
    "stream.preview": "Preview",
    "stream.cast": "Cast scene",

    // Reports
    "reports.title": "Reports",
    "reports.subtitle": "Integrity reports by session, exam, event and company.",
    "reports.exportPdf": "Export PDF",
    "reports.exportCsv": "Export CSV",

    // Alerts
    "alerts.title": "Alerts & Telemetry",
    "alerts.subtitle": "Suspicious events and system health with root-cause diagnosis.",
    "alerts.severity": "Severity",
    "alerts.cause": "Cause",
    "alerts.causeClient": "Client",
    "alerts.causeServer": "Server",
    "sev.critical": "Critical",
    "sev.high": "High",
    "sev.medium": "Medium",
    "sev.low": "Low",
    "sev.info": "Info",

    // Admin
    "admin.title": "Administration",
    "admin.companies": "Companies (tenants)",
    "admin.users": "Users & roles",
    "admin.integrations": "Integrations",
    "admin.retention": "Data retention",
    "admin.apiKeys": "API keys",
    "admin.audit": "Audit log",
    "admin.role.admin": "Admin",
    "admin.role.proctor": "Proctor",
    "admin.role.reviewer": "Reviewer",
    "admin.role.viewer": "Viewer",

    // Account
    "account.title": "Account settings",
    "account.profile": "Profile",
    "account.language": "Language",
    "account.mfa": "Multi-factor auth",
    "account.notifications": "Notifications",
    "account.billing": "Billing",

    // Modal / confirm
    "modal.confirmDelete.title": "Confirm deletion",
    "modal.confirmDelete.body": "This action cannot be undone. Are you sure?",
    "modal.endLive.title": "End live session",
    "modal.endLive.body": "The recording will be finalized and the live feed will stop.",

    // Footer
    "footer.tagline": "Verifiable integrity, at any scale.",
    "footer.product": "Product",
    "footer.verticals": "Verticals",
    "footer.company": "Company",
    "footer.legal": "Legal",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.dpa": "DPA",
    "footer.about": "About",
    "footer.careers": "Careers",
    "footer.rights": "All rights reserved.",
    "footer.madeBy": "A sec4us company",

    // Cookie banner
    "cookie.text": "We use cookies to run the site and improve your experience. Monitored users always receive a clear recording notice.",
    "cookie.accept": "Accept",
    "cookie.decline": "Decline",

    // 404
    "nf.title": "Page not found",
    "nf.body": "The page you’re looking for doesn’t exist or has moved.",
  },

  /* ===================================================================== */
  /* PORTUGUÊS (completo)                                                    */
  /* ===================================================================== */
  pt: {
    "topbar.tagline": "Integridade verificável, em qualquer escala.",
    "topbar.status": "Nível empresarial · Pronto para SOC 2 · LGPD & GDPR",

    "nav.home": "Início",
    "nav.exams": "Exames",
    "nav.ctf": "CTF + LiveStream",
    "nav.features": "Recursos",
    "nav.pricing": "Preços",
    "nav.security": "Segurança",
    "nav.integrations": "Integrações",
    "nav.docs": "Docs / API",
    "nav.blog": "Blog",
    "nav.contact": "Contato",
    "nav.login": "Entrar",
    "nav.getStarted": "Começar",
    "nav.bookDemo": "Agendar demo",
    "nav.openApp": "Abrir app",
    "nav.menu": "Menu",

    "common.learnMore": "Saiba mais",
    "common.startFree": "Iniciar teste grátis",
    "common.bookDemo": "Agendar demo",
    "common.contactSales": "Falar com vendas",
    "common.talkToUs": "Fale conosco",
    "common.readMore": "Ler mais",
    "common.viewAll": "Ver tudo",
    "common.all": "Todos",
    "common.recommended": "Recomendado",
    "common.monthly": "Mensal",
    "common.annual": "Anual",
    "common.annualHint": "2 meses grátis",
    "common.perMonth": "/mês",
    "common.close": "Fechar",
    "common.cancel": "Cancelar",
    "common.confirm": "Confirmar",
    "common.save": "Salvar",
    "common.delete": "Excluir",
    "common.remove": "Remover",
    "common.search": "Buscar",
    "common.export": "Exportar",
    "common.filter": "Filtrar",
    "common.loading": "Carregando…",
    "common.new": "Novo",
    "common.backHome": "Voltar ao início",
    "common.language": "Idioma",
    "common.theme": "Tema",
    "common.darkMode": "Modo escuro",
    "common.lightMode": "Modo claro",

    "home.hero.eyebrow": "Plataforma de proctoring remoto",
    "home.hero.title1": "Integridade verificável,",
    "home.hero.titleHighlight": "em qualquer escala.",
    "home.hero.lead":
      "Captura contínua de tela + câmera + áudio com carimbo de tempo do servidor à prova de fraude, gravação sem re-encode, revisão assistida por IA e transmissão ao vivo de baixa latência — projetada para escalar de uma única sessão a grandes eventos simultâneos.",
    "home.hero.ctaFree": "Iniciar teste grátis",
    "home.hero.ctaDemo": "Agendar demo",
    "home.hero.badge1": "sessões monitoradas",
    "home.hero.badge2": "horas gravadas",
    "home.hero.badge3": "transmissões simultâneas",
    "home.hero.livePanel": "Monitoramento ao vivo",
    "home.hero.liveTiles": "live grid · sharded",

    "home.verticals.eyebrow": "Escolha sua vertical",
    "home.verticals.title": "Um núcleo tecnológico, duas experiências dedicadas",
    "home.verticals.subtitle":
      "Proctoring de exames e antifraude de CTF compartilham o mesmo núcleo de captura à prova de fraude — com linguagem visual, fluxos e casos de uso distintos.",
    "home.verticals.exams.tag": "Para exames & certificações",
    "home.verticals.exams.title": "Proctoring de Exames",
    "home.verticals.exams.desc":
      "Monitore provas, certificações, concursos e avaliações acadêmicas ou corporativas com verificação de identidade guiada, detecção de eventos por IA e relatório de integridade por candidato.",
    "home.verticals.exams.cta": "Explorar Exames",
    "home.verticals.ctf.tag": "Para campeonatos CTF",
    "home.verticals.ctf.title": "CTF + LiveStream",
    "home.verticals.ctf.desc":
      "Monitoramento antifraude de competições de cibersegurança com Event Wall, integração CTFd/rCTF, overlays de transmissão e saída OBS de baixa latência.",
    "home.verticals.ctf.cta": "Explorar CTF",

    "home.features.eyebrow": "Por que SecProctoring",
    "home.features.title": "O núcleo técnico que sustenta a evidência",
    "home.features.subtitle": "Feito para escala, fidelidade e integridade forense.",
    "feat.capture.title": "Captura contínua",
    "feat.capture.desc": "Tela, webcam e áudio do ambiente gravados sem interrupção, do início ao fim.",
    "feat.timestamp.title": "Carimbo à prova de fraude",
    "feat.timestamp.desc": "Apenas relógio do servidor — a evidência nunca confia na hora do cliente.",
    "feat.noreencode.title": "Gravação sem re-encode",
    "feat.noreencode.desc": "Arquivos gap-free; overlay e encode adiados para um serviço separado.",
    "feat.ai.title": "Revisão assistida por IA",
    "feat.ai.desc": "Rosto ausente, múltiplas pessoas, olhar fora da tela, troca de aba, dispositivos secundários.",
    "feat.scale.title": "Escala massiva",
    "feat.scale.desc": "Sharding horizontal para live relay e gravação, distribuído por UUID.",
    "feat.live.title": "Live de baixa latência",
    "feat.live.desc": "Saída pública IP-gated e integração nativa com OBS para broadcast.",
    "feat.crossos.title": "Cross-OS, sem instalação",
    "feat.crossos.desc": "Captura via navegador por WebSocket/WebCodecs — nada a instalar, qualquer SO.",
    "feat.telemetry.title": "Telemetria por sessão",
    "feat.telemetry.desc": "Throughput de ingestão, codec negociado, detecção de fonte parada com causa raiz.",

    "home.stats.eyebrow": "Impacto em números",
    "home.stats.sessions": "Sessões monitoradas",
    "home.stats.hours": "Horas gravadas",
    "home.stats.frauds": "Fraudes sinalizadas",
    "home.stats.uptime": "Uptime de ingestão",

    "home.how.eyebrow": "Como funciona",
    "home.how.title": "Do check-in ao veredito em quatro passos",
    "home.how.s1.title": "Check-in & identidade",
    "home.how.s1.desc": "Foto do documento + selfie, comparação facial e teste de dispositivo pré-sessão.",
    "home.how.s2.title": "Captura contínua",
    "home.how.s2.desc": "Tela + câmera + áudio para ingestão com tokens de resume e keyframes.",
    "home.how.s3.title": "Revisão IA + humana",
    "home.how.s3.desc": "Timeline de eventos com marcadores de severidade; proctors acompanham uma grade ao vivo.",
    "home.how.s4.title": "Relatório de integridade",
    "home.how.s4.desc": "Score por sessão, clipes de evidência e veredito final, exportáveis.",

    "home.compliance.eyebrow": "Segurança & compliance",
    "home.compliance.title": "Confiança embutida em cada camada",
    "home.compliance.subtitle":
      "Criptografia em trânsito e em repouso, aviso claro de gravação aos monitorados, controles de retenção e exportação.",
    "home.compliance.lgpd": "Alinhado à LGPD & GDPR",
    "home.compliance.soc2": "Controles prontos para SOC 2",
    "home.compliance.encryption": "TLS em trânsito · AES em repouso",
    "home.compliance.dpa": "DPA disponível",

    "home.cta.title": "Pronto para provar integridade em escala?",
    "home.cta.subtitle": "Suba um piloto em minutos ou fale com nosso time sobre escala empresarial.",
    "home.video.eyebrow": "Veja em ação",
    "home.video.title": "Veja a SecProctoring em ação",
    "home.video.subtitle": "Um panorama rápido da captura contínua, do monitoramento ao vivo e do Event Wall.",

    "home.trusted.title": "Usado por equipes que operam avaliações e competições de alto risco",

    "exams.hero.eyebrow": "Proctoring de Exames",
    "exams.hero.title": "Provas justas, evidência defensável",
    "exams.hero.lead":
      "Para universidades, empresas de certificação, RH corporativo e órgãos de concurso: verificação de identidade guiada, captura contínua e detecção de eventos por IA com relatório de integridade por candidato.",
    "exams.f.identity.title": "Verificação de identidade",
    "exams.f.identity.desc": "Foto do documento + selfie, comparação facial e check-in guiado.",
    "exams.f.preflight.title": "Check de ambiente pré-prova",
    "exams.f.preflight.desc": "Teste de câmera, microfone, tela e velocidade de conexão antes de iniciar.",
    "exams.f.capture.title": "Tela + webcam + áudio",
    "exams.f.capture.desc": "Captura simultânea e contínua do início ao fim da prova.",
    "exams.f.timestamp.title": "Overlay de carimbo do servidor",
    "exams.f.timestamp.desc": "Evidência à prova de manipulação — nunca confia no relógio do cliente.",
    "exams.f.ai.title": "Detecção de eventos por IA",
    "exams.f.ai.desc": "Rosto ausente, múltiplas pessoas, olhar fora da tela, troca de aba, dispositivos secundários, copiar/colar.",
    "exams.f.modes.title": "Ao vivo, gravado ou híbrido",
    "exams.f.modes.desc": "Um proctor humano monitora uma grade, revisão posterior, ou ambos.",
    "exams.f.review.title": "Fila de revisão pós-prova",
    "exams.f.review.desc": "Timeline de eventos, filtros por severidade e exportação precisa de clipes.",
    "exams.f.rules.title": "Regras por exame",
    "exams.f.rules.desc": "Áudio obrigatório, resolução, tolerâncias, agenda e duração.",
    "exams.compare.title": "Com vs. sem SecProctoring",
    "exams.compare.without": "Sem",
    "exams.compare.with": "Com SecProctoring",
    "exams.faq.title": "Perguntas frequentes — Exames",

    "ctf.hero.eyebrow": "CTF + LiveStream",
    "ctf.hero.title": "Antifraude e broadcast para competições de segurança",
    "ctf.hero.lead":
      "Monitore competidores durante o CTF, alimente um Event Wall público com placar ao vivo e transmita para o público com baixa latência via OBS.",
    "ctf.f.anticheat.title": "Monitoramento antifraude",
    "ctf.f.anticheat.desc": "Tela + câmera + áudio garantem que as resoluções são do próprio competidor.",
    "ctf.f.integration.title": "Integração CTFd / rCTF",
    "ctf.f.integration.desc": "Ranking e posição em tempo real via integração nativa ou webhook.",
    "ctf.f.eventwall.title": "Event Wall",
    "ctf.f.eventwall.desc": "Mural público/privado com presets Top 10 spotlight e Todos, trocados ao vivo.",
    "ctf.f.stream.title": "LiveStream de baixa latência",
    "ctf.f.stream.desc": "Saída pública IP-gated e integração nativa com OBS para broadcast.",
    "ctf.f.overlays.title": "Overlays de transmissão",
    "ctf.f.overlays.desc": "Placar ao vivo, handle do competidor, spotlight de flag capturada e cronômetro.",
    "ctf.f.cast.title": "Botão Cast",
    "ctf.f.cast.desc": "Troque o template/cena da transmissão em tempo real pelo console.",
    "ctf.f.scale.title": "Escala de campeonato",
    "ctf.f.scale.desc": "Transmissões simultâneas e gravação completa para auditoria pós-evento.",
    "ctf.audience.organizers": "Para organizadores",
    "ctf.audience.public": "Para o público",

    "features.hero.eyebrow": "Núcleo tecnológico",
    "features.hero.title": "Projetado para integridade forense",
    "features.hero.lead": "As capacidades por trás de cada sessão SecProctoring — acessíveis, com um aprofundamento “para técnicos”.",
    "features.forEngineers": "Para técnicos",
    "features.arch.title": "Arquitetura em resumo",
    "features.arch.client": "Cliente (browser)",
    "features.arch.ingest": "Ingestão (WebSocket/ASGI)",
    "features.arch.recording": "Gravação (buckets crus)",
    "features.arch.renderer": "Renderer (overlay + encode)",
    "features.arch.review": "Revisão / DB",
    "features.arch.relay": "Live relay",
    "features.arch.output": "Event Wall / OBS",

    "pricing.hero.eyebrow": "Preços",
    "pricing.hero.title": "Planos que escalam com sua necessidade de integridade",
    "pricing.hero.lead": "Alterne entre verticais e ciclos de cobrança. Escala empresarial é sempre uma conversa.",
    "pricing.toggle.exams": "Exames",
    "pricing.toggle.ctf": "CTF + LiveStream",
    "pricing.mostPopular": "Mais popular",
    "pricing.customPrice": "Fale conosco",
    "pricing.cta.start": "Começar grátis",
    "pricing.cta.contact": "Falar com vendas",
    "pricing.compare.title": "Comparar planos",
    "pricing.faq.title": "FAQ de cobrança",

    "plan.starter.name": "Starter",
    "plan.starter.desc": "Gravação + revisão para equipes pequenas.",
    "plan.pro.name": "Pro",
    "plan.pro.desc": "Detecção com IA, ao vivo + gravado, LMS/SSO.",
    "plan.business.name": "Business",
    "plan.business.desc": "Multi-tenant, API/webhooks, SLA.",
    "plan.enterprise.name": "Enterprise",
    "plan.enterprise.desc": "Escala massiva, on-premise, DPA, SAML.",
    "plan.community.name": "Community",
    "plan.community.desc": "Eventos pequenos, live pública básica.",
    "plan.competition.name": "Competition",
    "plan.competition.desc": "Event Wall, CTFd/rCTF, OBS output.",
    "plan.championship.name": "Championship",
    "plan.championship.desc": "Grandes eventos simultâneos.",
    "plan.whitelabel.name": "Enterprise / White-label",
    "plan.whitelabel.desc": "Marca própria, dedicado, sob medida.",

    "security.hero.eyebrow": "Segurança & Compliance",
    "security.hero.title": "Evidência que você defende, dados em que você confia",
    "security.hero.lead": "Arquitetura de segurança, políticas, certificações e um Acordo de Processamento de Dados (DPA).",

    "contact.hero.eyebrow": "Contato & demo",
    "contact.hero.title": "Veja a SecProctoring nos seus próprios exames ou evento",
    "contact.hero.lead": "Conte sobre seu caso de uso e adaptamos uma demo. Fale com a gente por e-mail ou WhatsApp — respondemos em até um dia útil.",
    "contact.channels.title": "Escolha como falar com a gente",
    "contact.channels.subtitle": "Sem formulários. Envie um e-mail ou inicie uma conversa no WhatsApp — respondemos em até um dia útil.",
    "contact.email.title": "E-mail",
    "contact.email.cta": "Enviar e-mail",
    "contact.whatsapp.title": "WhatsApp",
    "contact.whatsapp.desc": "Fale com nosso time agora.",
    "contact.whatsapp.cta": "Abrir WhatsApp",
    "contact.form.name": "Nome completo",
    "contact.form.email": "E-mail corporativo",
    "contact.form.company": "Empresa",
    "contact.form.vertical": "Vertical de interesse",
    "contact.form.verticalExams": "Proctoring de exames",
    "contact.form.verticalCtf": "CTF + LiveStream",
    "contact.form.verticalBoth": "Ambas",
    "contact.form.scale": "Escala esperada (sessões simultâneas)",
    "contact.form.message": "Como podemos ajudar?",
    "contact.form.submit": "Solicitar demo",
    "contact.form.consent": "Concordo com o tratamento dos meus dados conforme a Política de Privacidade.",
    "contact.success.title": "Solicitação recebida",
    "contact.success.body": "Obrigado! Nosso time entrará em contato em até um dia útil para agendar sua demo.",

    "auth.login.title": "Entrar na SecProctoring",
    "auth.login.subtitle": "Acesse o workspace da sua empresa.",
    "auth.login.email": "E-mail",
    "auth.login.password": "Senha",
    "auth.login.remember": "Manter conectado",
    "auth.login.forgot": "Esqueceu a senha?",
    "auth.login.submit": "Entrar",
    "auth.login.sso": "Continuar com SSO",
    "auth.login.mfaNote": "Protegido por MFA",
    "auth.login.noAccount": "Não tem conta?",
    "auth.login.demoHint": "Build de demonstração — qualquer e-mail/senha entra no app.",

    "app.nav.overview": "Visão geral",
    "app.nav.monitoring": "Monitoramento ao vivo",
    "app.nav.eventWall": "Event Wall",
    "app.nav.sessions": "Sessões",
    "app.nav.review": "Revisão de sessão",
    "app.nav.examsEvents": "Exames / Eventos",
    "app.nav.people": "Candidatos / Competidores",
    "app.nav.stream": "Config. de transmissão",
    "app.nav.reports": "Relatórios",
    "app.nav.alerts": "Alertas & Telemetria",
    "app.nav.admin": "Administração",
    "app.nav.account": "Configurações da conta",
    "app.nav.backToSite": "Voltar ao site",
    "app.nav.logout": "Sair",
    "app.company": "Empresa",
    "app.notifications": "Notificações",

    "dash.title": "Visão geral",
    "dash.subtitle": "Postura em tempo real do seu tenant.",
    "dash.kpi.activeSessions": "Sessões ativas",
    "dash.kpi.liveStreams": "Transmissões ao vivo",
    "dash.kpi.openAlerts": "Alertas abertos",
    "dash.kpi.hoursRecorded": "Horas gravadas",
    "dash.kpi.throughput": "Throughput médio (1 min)",
    "dash.chart.sessions": "Sessões ao longo do tempo",
    "dash.recentEvents": "Eventos recentes",
    "dash.systemHealth": "Saúde do sistema",
    "dash.shards": "Shards",
    "dash.ingest": "Ingestão",
    "dash.renderer": "Renderer",
    "dash.relay": "Live relay",

    "mon.title": "Monitoramento ao vivo",
    "mon.subtitle": "Tiles de câmera + tela das sessões ativas.",
    "mon.layout": "Layout",
    "mon.focus": "Foco",
    "mon.searchPlaceholder": "Buscar candidato ou sessão…",
    "mon.alert": "Alerta",
    "mon.healthy": "Saudável",
    "mon.cast": "Cast",
    "mon.preset": "Preset",
    "mon.presetTop10": "Top 10 spotlight",
    "mon.presetAll": "Todos",
    "mon.score": "Score",

    "sessions.title": "Sessões",
    "sessions.subtitle": "Toda sessão de captura entre exames e eventos.",
    "sessions.col.subject": "Candidato / Competidor",
    "sessions.col.exam": "Exame / Evento",
    "sessions.col.company": "Empresa",
    "sessions.col.status": "Status",
    "sessions.col.start": "Início",
    "sessions.col.duration": "Duração",
    "sessions.col.events": "Eventos",
    "sessions.col.integrity": "Integridade",
    "sessions.status.live": "Ao vivo",
    "sessions.status.recorded": "Gravado",
    "sessions.status.review": "Em revisão",
    "sessions.status.done": "Concluído",
    "sessions.bulkExport": "Exportar selecionados",

    "review.title": "Revisão de sessão",
    "review.timeline": "Timeline de eventos",
    "review.events": "Eventos",
    "review.sources": "Fontes",
    "review.screen": "Tela",
    "review.camera": "Câmera",
    "review.audio": "Áudio",
    "review.clip": "Exportar clipe",
    "review.notes": "Anotações do revisor",
    "review.verdict": "Veredito de integridade",
    "review.verdict.clean": "Limpo",
    "review.verdict.review": "Requer revisão",
    "review.verdict.flagged": "Sinalizado",
    "review.saveVerdict": "Salvar veredito",

    "ee.title": "Exames / Eventos",
    "ee.new": "Novo exame / evento",
    "ee.form.name": "Nome",
    "ee.form.company": "Empresa",
    "ee.form.schedule": "Agenda",
    "ee.form.duration": "Duração (min)",
    "ee.form.mode": "Modo",
    "ee.form.modeLive": "Ao vivo",
    "ee.form.modeRecorded": "Gravado",
    "ee.form.modeHybrid": "Híbrido",
    "ee.form.requireAudio": "Áudio obrigatório",
    "ee.form.resolution": "Resolução máxima",
    "ee.form.tolerance": "Tolerância da IA",
    "ee.form.integrations": "Integrações",
    "ee.form.save": "Salvar exame / evento",

    "stream.title": "Configuração de transmissão",
    "stream.subtitle": "Saídas OBS, URL pública IP-gated e templates de cena.",
    "stream.ndi": "NDI (LAN)",
    "stream.srt": "SRT / RTSP (remoto)",
    "stream.public": "URL pública IP-gated",
    "stream.templates": "Templates de cena",
    "stream.preview": "Preview",
    "stream.cast": "Cast cena",

    "reports.title": "Relatórios",
    "reports.subtitle": "Relatórios de integridade por sessão, exame, evento e empresa.",
    "reports.exportPdf": "Exportar PDF",
    "reports.exportCsv": "Exportar CSV",

    "alerts.title": "Alertas & Telemetria",
    "alerts.subtitle": "Eventos suspeitos e saúde do sistema com diagnóstico de causa raiz.",
    "alerts.severity": "Severidade",
    "alerts.cause": "Causa",
    "alerts.causeClient": "Cliente",
    "alerts.causeServer": "Servidor",
    "sev.critical": "Crítico",
    "sev.high": "Alto",
    "sev.medium": "Médio",
    "sev.low": "Baixo",
    "sev.info": "Info",

    "admin.title": "Administração",
    "admin.companies": "Empresas (tenants)",
    "admin.users": "Usuários & papéis",
    "admin.integrations": "Integrações",
    "admin.retention": "Retenção de dados",
    "admin.apiKeys": "Chaves de API",
    "admin.audit": "Log de auditoria",
    "admin.role.admin": "Admin",
    "admin.role.proctor": "Proctor",
    "admin.role.reviewer": "Revisor",
    "admin.role.viewer": "Visualizador",

    "account.title": "Configurações da conta",
    "account.profile": "Perfil",
    "account.language": "Idioma",
    "account.mfa": "Autenticação multifator",
    "account.notifications": "Notificações",
    "account.billing": "Faturamento",

    "modal.confirmDelete.title": "Confirmar exclusão",
    "modal.confirmDelete.body": "Esta ação não pode ser desfeita. Tem certeza?",
    "modal.endLive.title": "Encerrar sessão ao vivo",
    "modal.endLive.body": "A gravação será finalizada e o feed ao vivo será interrompido.",

    "footer.tagline": "Integridade verificável, em qualquer escala.",
    "footer.product": "Produto",
    "footer.verticals": "Verticais",
    "footer.company": "Empresa",
    "footer.legal": "Legal",
    "footer.privacy": "Política de Privacidade",
    "footer.terms": "Termos de Serviço",
    "footer.dpa": "DPA",
    "footer.about": "Sobre",
    "footer.careers": "Carreiras",
    "footer.rights": "Todos os direitos reservados.",
    "footer.madeBy": "Uma empresa sec4us",

    "cookie.text": "Usamos cookies para operar o site e melhorar sua experiência. Usuários monitorados sempre recebem aviso claro de gravação.",
    "cookie.accept": "Aceitar",
    "cookie.decline": "Recusar",

    "nf.title": "Página não encontrada",
    "nf.body": "A página que você procura não existe ou foi movida.",
  },

  /* ===================================================================== */
  /* ESPAÑOL (navegación + marketing chave)                                 */
  /* ===================================================================== */
  es: {
    "topbar.tagline": "Integridad verificable, a cualquier escala.",
    "topbar.status": "Nivel empresarial · Listo para SOC 2 · LGPD & GDPR",

    "nav.home": "Inicio",
    "nav.exams": "Exámenes",
    "nav.ctf": "CTF + LiveStream",
    "nav.features": "Recursos",
    "nav.pricing": "Precios",
    "nav.security": "Seguridad",
    "nav.integrations": "Integraciones",
    "nav.docs": "Docs / API",
    "nav.blog": "Blog",
    "nav.contact": "Contacto",
    "nav.login": "Entrar",
    "nav.getStarted": "Empezar",
    "nav.bookDemo": "Agendar demo",
    "nav.openApp": "Abrir app",
    "nav.menu": "Menú",

    "common.learnMore": "Saber más",
    "common.startFree": "Prueba gratuita",
    "common.bookDemo": "Agendar demo",
    "common.contactSales": "Hablar con ventas",
    "common.talkToUs": "Contáctanos",
    "common.readMore": "Leer más",
    "common.viewAll": "Ver todo",
    "common.all": "Todos",
    "common.recommended": "Recomendado",
    "common.monthly": "Mensual",
    "common.annual": "Anual",
    "common.annualHint": "2 meses gratis",
    "common.perMonth": "/mes",
    "common.close": "Cerrar",
    "common.cancel": "Cancelar",
    "common.confirm": "Confirmar",
    "common.save": "Guardar",
    "common.delete": "Eliminar",
    "common.remove": "Quitar",
    "common.search": "Buscar",
    "common.export": "Exportar",
    "common.filter": "Filtrar",
    "common.loading": "Cargando…",
    "common.new": "Nuevo",
    "common.backHome": "Volver al inicio",
    "common.language": "Idioma",
    "common.theme": "Tema",
    "common.darkMode": "Modo oscuro",
    "common.lightMode": "Modo claro",

    "home.hero.eyebrow": "Plataforma de proctoring remoto",
    "home.hero.title1": "Integridad verificable,",
    "home.hero.titleHighlight": "a cualquier escala.",
    "home.hero.lead":
      "Captura continua de pantalla + cámara + audio con sello de tiempo del servidor a prueba de fraude, grabación sin re-encode, revisión asistida por IA y streaming en vivo de baja latencia — diseñada para escalar de una sesión a grandes eventos simultáneos.",
    "home.hero.ctaFree": "Prueba gratuita",
    "home.hero.ctaDemo": "Agendar demo",
    "home.hero.badge1": "sesiones monitoreadas",
    "home.hero.badge2": "horas grabadas",
    "home.hero.badge3": "transmisiones simultáneas",
    "home.hero.livePanel": "Monitoreo en vivo",
    "home.hero.liveTiles": "live grid · sharded",

    "home.verticals.eyebrow": "Elige tu vertical",
    "home.verticals.title": "Un núcleo, dos experiencias dedicadas",
    "home.verticals.subtitle":
      "El proctoring de exámenes y el anti-fraude de CTF comparten el mismo núcleo de captura a prueba de manipulación, con lenguaje visual y flujos distintos.",
    "home.verticals.exams.tag": "Para exámenes y certificaciones",
    "home.verticals.exams.title": "Proctoring de Exámenes",
    "home.verticals.exams.desc":
      "Monitorea exámenes, certificaciones, oposiciones y evaluaciones con verificación de identidad guiada, detección por IA e informe de integridad por candidato.",
    "home.verticals.exams.cta": "Explorar Exámenes",
    "home.verticals.ctf.tag": "Para campeonatos CTF",
    "home.verticals.ctf.title": "CTF + LiveStream",
    "home.verticals.ctf.desc":
      "Monitoreo anti-fraude de competiciones de ciberseguridad con Event Wall, integración CTFd/rCTF, overlays y salida OBS de baja latencia.",
    "home.verticals.ctf.cta": "Explorar CTF",

    "home.features.eyebrow": "Por qué SecProctoring",
    "home.features.title": "El núcleo técnico que sostiene la evidencia",
    "home.features.subtitle": "Hecho para escala, fidelidad e integridad forense.",

    "home.stats.eyebrow": "Impacto en números",
    "home.stats.sessions": "Sesiones monitoreadas",
    "home.stats.hours": "Horas grabadas",
    "home.stats.frauds": "Fraudes señalados",
    "home.stats.uptime": "Uptime de ingesta",

    "home.how.eyebrow": "Cómo funciona",
    "home.how.title": "Del check-in al veredicto en cuatro pasos",

    "home.cta.title": "¿Listo para probar integridad a escala?",
    "home.cta.subtitle": "Lanza un piloto en minutos o habla con nuestro equipo sobre escala empresarial.",
    "home.trusted.title": "Usado por equipos que operan evaluaciones y competiciones de alto riesgo",

    "pricing.hero.title": "Planes que escalan con tu necesidad de integridad",
    "pricing.mostPopular": "Más popular",
    "pricing.customPrice": "Hablemos",

    "app.nav.overview": "Resumen",
    "app.nav.monitoring": "Monitoreo en vivo",
    "app.nav.eventWall": "Event Wall",
    "app.nav.sessions": "Sesiones",
    "app.nav.review": "Revisión de sesión",
    "app.nav.examsEvents": "Exámenes / Eventos",
    "app.nav.people": "Candidatos / Competidores",
    "app.nav.stream": "Config. de transmisión",
    "app.nav.reports": "Informes",
    "app.nav.alerts": "Alertas y Telemetría",
    "app.nav.admin": "Administración",
    "app.nav.account": "Ajustes de cuenta",
    "app.nav.backToSite": "Volver al sitio",
    "app.nav.logout": "Salir",
    "app.company": "Empresa",

    "footer.tagline": "Integridad verificable, a cualquier escala.",
    "footer.product": "Producto",
    "footer.verticals": "Verticales",
    "footer.company": "Empresa",
    "footer.legal": "Legal",
    "footer.privacy": "Política de Privacidad",
    "footer.terms": "Términos de Servicio",
    "footer.rights": "Todos los derechos reservados.",
    "footer.madeBy": "Una empresa sec4us",

    "cookie.text": "Usamos cookies para operar el sitio y mejorar tu experiencia. Los usuarios monitoreados siempre reciben aviso claro de grabación.",
    "cookie.accept": "Aceptar",
    "cookie.decline": "Rechazar",

    "nf.title": "Página no encontrada",
    "nf.body": "La página que buscas no existe o se ha movido.",
  },

  /* ===================================================================== */
  /* ITALIANO (navigazione + marketing chiave)                              */
  /* ===================================================================== */
  it: {
    "topbar.tagline": "Integrità verificabile, a qualsiasi scala.",
    "topbar.status": "Livello enterprise · Pronto per SOC 2 · LGPD & GDPR",

    "nav.home": "Home",
    "nav.exams": "Esami",
    "nav.ctf": "CTF + LiveStream",
    "nav.features": "Funzionalità",
    "nav.pricing": "Prezzi",
    "nav.security": "Sicurezza",
    "nav.integrations": "Integrazioni",
    "nav.docs": "Docs / API",
    "nav.blog": "Blog",
    "nav.contact": "Contatti",
    "nav.login": "Accedi",
    "nav.getStarted": "Inizia",
    "nav.bookDemo": "Prenota demo",
    "nav.openApp": "Apri app",
    "nav.menu": "Menu",

    "common.learnMore": "Scopri di più",
    "common.startFree": "Prova gratuita",
    "common.bookDemo": "Prenota demo",
    "common.contactSales": "Contatta le vendite",
    "common.talkToUs": "Contattaci",
    "common.readMore": "Leggi di più",
    "common.viewAll": "Vedi tutto",
    "common.all": "Tutti",
    "common.recommended": "Consigliato",
    "common.monthly": "Mensile",
    "common.annual": "Annuale",
    "common.annualHint": "2 mesi gratis",
    "common.perMonth": "/mese",
    "common.close": "Chiudi",
    "common.cancel": "Annulla",
    "common.confirm": "Conferma",
    "common.save": "Salva",
    "common.delete": "Elimina",
    "common.remove": "Rimuovi",
    "common.search": "Cerca",
    "common.export": "Esporta",
    "common.filter": "Filtra",
    "common.loading": "Caricamento…",
    "common.new": "Nuovo",
    "common.backHome": "Torna alla home",
    "common.language": "Lingua",
    "common.theme": "Tema",
    "common.darkMode": "Modalità scura",
    "common.lightMode": "Modalità chiara",

    "home.hero.eyebrow": "Piattaforma di proctoring remoto",
    "home.hero.title1": "Integrità verificabile,",
    "home.hero.titleHighlight": "a qualsiasi scala.",
    "home.hero.lead":
      "Cattura continua di schermo + camera + audio con timestamp del server a prova di manomissione, registrazione senza re-encode, revisione assistita da IA e streaming live a bassa latenza — progettata per scalare da una sessione a grandi eventi simultanei.",
    "home.hero.ctaFree": "Prova gratuita",
    "home.hero.ctaDemo": "Prenota demo",
    "home.hero.badge1": "sessioni monitorate",
    "home.hero.badge2": "ore registrate",
    "home.hero.badge3": "trasmissioni simultanee",
    "home.hero.livePanel": "Monitoraggio live",
    "home.hero.liveTiles": "live grid · sharded",

    "home.verticals.eyebrow": "Scegli la tua verticale",
    "home.verticals.title": "Un core, due esperienze dedicate",
    "home.verticals.subtitle":
      "Il proctoring degli esami e l’anti-frode CTF condividono lo stesso core di cattura a prova di manomissione, con linguaggio visivo e flussi distinti.",
    "home.verticals.exams.tag": "Per esami e certificazioni",
    "home.verticals.exams.title": "Proctoring Esami",
    "home.verticals.exams.desc":
      "Monitora esami, certificazioni, concorsi e valutazioni con verifica dell’identità guidata, rilevamento IA e report di integrità per candidato.",
    "home.verticals.exams.cta": "Esplora Esami",
    "home.verticals.ctf.tag": "Per campionati CTF",
    "home.verticals.ctf.title": "CTF + LiveStream",
    "home.verticals.ctf.desc":
      "Monitoraggio anti-frode di competizioni di cybersecurity con Event Wall, integrazione CTFd/rCTF, overlay e uscita OBS a bassa latenza.",
    "home.verticals.ctf.cta": "Esplora CTF",

    "home.features.eyebrow": "Perché SecProctoring",
    "home.features.title": "Il core tecnico che sostiene la prova",
    "home.features.subtitle": "Costruito per scala, fedeltà e integrità forense.",

    "home.stats.eyebrow": "Impatto in numeri",
    "home.stats.sessions": "Sessioni monitorate",
    "home.stats.hours": "Ore registrate",
    "home.stats.frauds": "Frodi segnalate",
    "home.stats.uptime": "Uptime di ingest",

    "home.how.eyebrow": "Come funziona",
    "home.how.title": "Dal check-in al verdetto in quattro passi",

    "home.cta.title": "Pronto a dimostrare integrità su scala?",
    "home.cta.subtitle": "Avvia un pilota in minuti o parla col nostro team di scala enterprise.",
    "home.trusted.title": "Usato da team che gestiscono valutazioni e competizioni ad alto rischio",

    "pricing.hero.title": "Piani che scalano con le tue esigenze di integrità",
    "pricing.mostPopular": "Più popolare",
    "pricing.customPrice": "Parliamone",

    "app.nav.overview": "Panoramica",
    "app.nav.monitoring": "Monitoraggio live",
    "app.nav.eventWall": "Event Wall",
    "app.nav.sessions": "Sessioni",
    "app.nav.review": "Revisione sessione",
    "app.nav.examsEvents": "Esami / Eventi",
    "app.nav.people": "Candidati / Competitori",
    "app.nav.stream": "Config. trasmissione",
    "app.nav.reports": "Report",
    "app.nav.alerts": "Avvisi e Telemetria",
    "app.nav.admin": "Amministrazione",
    "app.nav.account": "Impostazioni account",
    "app.nav.backToSite": "Torna al sito",
    "app.nav.logout": "Esci",
    "app.company": "Azienda",

    "footer.tagline": "Integrità verificabile, a qualsiasi scala.",
    "footer.product": "Prodotto",
    "footer.verticals": "Verticali",
    "footer.company": "Azienda",
    "footer.legal": "Legale",
    "footer.privacy": "Informativa privacy",
    "footer.terms": "Termini di servizio",
    "footer.rights": "Tutti i diritti riservati.",
    "footer.madeBy": "Un’azienda sec4us",

    "cookie.text": "Usiamo cookie per far funzionare il sito e migliorare la tua esperienza. Gli utenti monitorati ricevono sempre un chiaro avviso di registrazione.",
    "cookie.accept": "Accetta",
    "cookie.decline": "Rifiuta",

    "nf.title": "Pagina non trovata",
    "nf.body": "La pagina che cerchi non esiste o è stata spostata.",
  },
};
